import Service from '@ember/service';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import Utils from '../utils/utils';

/**
 * This service sets the initial selected data
 */
export default class HashSelectedDataService extends Service {
    @service hashService;
    @service urlData;
    @service selectionData;
    @service partData;
    @service validation;
    @service('aditools-analytics') analytics;

    init() {
        super.init();

        let selectionData = this.selectionData;
        selectionData.setProperties(this.modelData);

        this.validation.run();
    }

    /**
     * Get the selected ADC
     * We first look to see whether:
     *    1. The adc was in the route
     *    2. The user was directed to the app via the product page
     *    3. The adc was in the hash
     *
     * If the adc was not found, we set it via the tool default.
     */
    @computed('hashService.parsedLocationHash', 'selectionData.{adcs,defaultAdc}') get adc() {
        let hashValues = this.hashService.parsedLocationHash,
            adcOptions = this.selectionData.adcs,
            foundAdcParamInRoute = this.urlData.findAdcParam(),
            foundReferrerAdc = this._findAdcFromReferrerUrl(),
            foundHashAdc = typeof hashValues.adc !== 'undefined' ? hashValues.adc : null;

        // Set values from url or default
        // TODO: Move all inputs into a values service.. and move all of the default setting into the hash service.
        // ADC
        let foundAdc = foundHashAdc || foundReferrerAdc || foundAdcParamInRoute,
            filteredAdc = foundAdc
                ? adcOptions.filter((adc) => adc.part.toUpperCase() === foundAdc.toUpperCase())
                : null;

        if (filteredAdc && filteredAdc.length === 1) {
            return filteredAdc[0];
        } else {
            let defaultAdc = adcOptions.filter((adc) => adc.part === this.selectionData.defaultAdc);
            if (defaultAdc && defaultAdc.length === 1) {
                return defaultAdc[0];
            } else {
                // If all else fails
                return adcOptions[0];
            }
        }
    }

    /**
     * Set the selected Driver
     *
     * We first determine the following:
     *      1. Is the driver in the hash
     *      2. Does the selectedAdc have a default driver
     *
     * If the driver was not found, then was use the default driver set by the tool
     */
    @computed('hashService.parsedLocationHash', 'selectionData.{drivers,defaultDriver}', 'adc') get driver() {
        let defaultDriver = this.adc.defaultDriver || this.selectionData.defaultDriver,
            hashValues = this.hashService.parsedLocationHash,
            driverOptions = this.selectionData.drivers,
            foundDefaultDriver = driverOptions.filter((driver) => driver.part === defaultDriver),
            foundHashDriver = null;

        if (typeof hashValues.driver !== 'undefined') {
            foundHashDriver = driverOptions.filter((driver) => driver.part === hashValues.driver);
        }

        if (foundHashDriver && foundHashDriver.length === 1) {
            return foundHashDriver[0];
        } else if (foundDefaultDriver && foundDefaultDriver.length === 1) {
            return foundDefaultDriver[0];
        } else {
            // if all else has failed
            return driverOptions[0];
        }
    }

    @computed('hashService.parsedLocationHash', 'selectionData.topologyOptions') get topology() {
        return this._getSelectedOptionFromHash('topology').value;
    }

    @computed('hashService.parsedLocationHash', 'selectionData.sourceTypeOptions', 'adc') get sourceType() {
        let selectedSourceType = this._getSelectedOptionFromHash('sourceType');

        if (selectedSourceType.from === 'url') {
            return selectedSourceType.value;
        } else {
            return this.selectionData.findSourceTypeFromAdcInputType(this.adc.type);
        }
    }

    @computed('hashService.parsedLocationHash', 'selectionData.lsbSettlingOptions') get lsbSettling() {
        return this._getSelectedOptionFromHash('lsbSettling').value;
    }

    @computed('adc', 'driver', 'topology', 'sourceType', 'lsbSettling') get modelData() {
        return {
            selectedAdc: this.adc,
            selectedDriver: this.driver,
            selectedTopology: this.topology,
            selectedSourceType: this.sourceType,
            selectedLsbSettling: this.lsbSettling,
        };
    }

    _getSelectedOptionFromHash(optionsType) {
        let hashValues = this.hashService.parsedLocationHash,
            selectionData = this.selectionData,
            hashValue = hashValues[optionsType],
            value = null,
            from = null;

        if (typeof hashValue !== 'undefined' && Utils.isNumeric(hashValue)) {
            from = 'url';
            value = selectionData.findOneOptionById(optionsType, Number(hashValue));
        }

        if (!value) {
            from = 'default';
            value = selectionData.findDefaultByOptionsType(optionsType);
        }

        return {
            from: from,
            value: value,
        };
    }

    _findAdcFromReferrerUrl() {
        let referrer = document.referrer,
            foundReferrerAdc = null,
            adcs = this.selectionData.adcs;
        if (referrer) {
            let referrerParts = referrer.split('/');

            for (var x = referrerParts.length - 1; x >= 0; x--) {
                let referrerPart = referrerParts[x];
                let referrerPartParts = referrerPart.split('.');
                for (var y = 0; y < referrerPartParts.length; y++) {
                    let foundAdc = adcs.filter((adc) => adc.part.toUpperCase() === referrerPartParts[y].toUpperCase());
                    if (foundAdc && foundAdc.length > 0) {
                        foundReferrerAdc = foundAdc[0].part;

                        this.analytics.setWebEvent(
                            this.partData.toolName,
                            'adcLoadedFromReferringPage',
                            foundReferrerAdc,
                            true,
                        );
                        break;
                    }
                }
            }
        }

        return foundReferrerAdc;
    }
}
